import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTimeFrame, isSupportedTimeFrame } from '../contexts/TimeFrameContext';
import {
  CircleChevronRight, ArrowLeft, Star, ChevronRight, ChevronLeft,
  BarChart2, TrendingUp, MessageSquare, Search, Brain, AlertTriangle,
  Zap, Languages, Info
} from 'lucide-react';
import SearchModal from './SearchModal/SearchModal';
import { AnimatedCircularProgress } from './Progress/AnimatedCircularProgress';
import { TradingJournal } from './Journal/TradingJournal';
import FullAnalysisModal from './FullAnalysisModal/FullAnalysisModal';
import DetailTradingView from './TradingView/DetailTradingView';
import InvestmentDisclaimer from './Common/InvestmentDisclaimer';
import ContactFooter from './Common/ContactFooter';
import {
  getTechnicalAnalysis,
  getCommunityAnalysis,
  getRiskAnalysis,
  getCoinDetailTechnical,
  getCoinDetailCommunity,
  getCoinDetailRisk,
  getCoinDetailSummary,
  getCoinDetailRunAt,
  getSummaryReport,
  toggleFavorite,
  getFavoriteList,
  getRefreshToken,
  isOAuthUser,
  getPlatform,
  requestAppReview,
  getNativeStorage,
  setNativeStorage,
  openExternalUrl,
} from '../services/api';
import './CoinDetailPage.css';
import AlertModal, { useAlert } from './Common/Alert';

const CoinDetailPage = () => {
  const [isSearchModalOpen, setIsSearchModalOpen]       = useState(false);
  const [isFullAnalysisModalOpen, setIsFullAnalysisModalOpen] = useState(false);
  const [currentReportType, setCurrentReportType]       = useState('technical');
  const [summaryData, setSummaryData]                   = useState(null);
  const [technicalData, setTechnicalData]               = useState(null);
  const [communityData, setCommunityData]               = useState(null);
  const [riskData, setRiskData]                         = useState(null);
  const [stockDetailTechnical, setStockDetailTechnical] = useState(null);
  const [stockDetailCommunity, setStockDetailCommunity] = useState(null);
  const [stockDetailRisk, setStockDetailRisk]           = useState(null);
  const [stockDetailSummary, setStockDetailSummary]     = useState(null);
  const [communityCurrentPage, setCommunityCurrentPage] = useState(1);
  const [showTooltip, setShowTooltip]                   = useState(null);
  const [loading, setLoading]                           = useState(true);
  const [error, setError]                               = useState(null);
  const [isFavorite, setIsFavorite]                     = useState(false);
  const [favoriteLoading, setFavoriteLoading]           = useState(false);
  const { alert, showAlert, closeAlert, showAuthAlert } = useAlert();

  const { symbol }  = useParams();
  const navigate    = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { timeFrame, setTimeFrame } = useTimeFrame();

  // URL ?tf= 가 있으면 Context와 동기화 (딥링크 경로용). URL이 없으면 Context 값을 URL에 반영.
  // 백엔드 미지원 timeFrame은 무시한다.
  useEffect(() => {
    const urlTf = searchParams.get('tf');
    if (urlTf && urlTf !== timeFrame && isSupportedTimeFrame(urlTf)) {
      setTimeFrame(urlTf);
    } else if (!urlTf && timeFrame) {
      const next = new URLSearchParams(searchParams);
      next.set('tf', timeFrame);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    document.title = `${symbol} 분석 | 코인특공대`;
    return () => { document.title = '코인특공대'; };
  }, [symbol]);

  useEffect(() => {
    setCommunityCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const checkAppRating = async () => {
      if (getPlatform() === 'web') return;
      const hasRated = await getNativeStorage('hasRatedApp');
      if (hasRated) return;
      const countStr = await getNativeStorage('detailPageViewCount');
      const count = parseInt(countStr || '0') + 1;
      setNativeStorage('detailPageViewCount', String(count));
      if (count >= 5) {
        setNativeStorage('hasRatedApp', 'true');
        requestAppReview();
      }
    };
    checkAppRating();
  }, [symbol]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/coinMain');
  };

  const handleStockClick = (ticker) => navigate(`/coinDetail/${ticker}/`);

  const openSearchModal = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setIsSearchModalOpen(true);
    return false;
  };

  const closeSearchModal = () => setIsSearchModalOpen(false);

  const handleExternalLink = (url) => {
    const device = getPlatform();
    if (device === 'android') { window.openExternalUrl?.postMessage(url); return; }
    if (window.webkit?.messageHandlers?.openExternalUrl) {
      window.webkit.messageHandlers.openExternalUrl.postMessage(url);
      return;
    }
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) window.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleTranslate = (text) => {
    const url = `https://translate.google.com/?sl=en&tl=ko&text=${encodeURIComponent(text)}&op=translate`;
    handleExternalLink(url);
  };

  const checkFavoriteStatus = async () => {
    if (!symbol) return;
    try {
      const favoriteList = await getFavoriteList();
      const found = favoriteList.list?.find(item => item.ticker === symbol);
      setIsFavorite(!!found);
    } catch {
      setIsFavorite(false);
    }
  };

  const handleKakaoShare = (sectionTitle, summary) => {
    const kakao = window.Kakao;
    if (!kakao) return;
    if (!kakao.isInitialized()) kakao.init('3a612caf3635160cef3ec4542c0b8344');
    const cleanSummary = summary
      ? summary.replace(/[#*_~`>]/g, '').trim().slice(0, 100) + '...'
      : `${symbol} AI 분석을 확인하세요.`;
    kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: `코인특공대 · ${symbol} ${sectionTitle}`,
        description: cleanSummary,
        imageUrl: `${window.location.origin}/social-share.png`,
        link: {
          webUrl: `https://mmstudio.co.kr:2443/coinDetail/${symbol}`,
          mobileWebUrl: `https://mmstudio.co.kr:2443/appStore?ticker=${symbol}`,
        },
      },
      buttons: [{
        title: '앱에서 보기',
        link: {
          webUrl: `https://mmstudio.co.kr:2443/coinDetail/${symbol}`,
          mobileWebUrl: `https://mmstudio.co.kr:2443/appStore?ticker=${symbol}`,
        },
      }],
    });
  };

  const handleFullAnalysisClick = (reportType) => {
    const token = getRefreshToken();
    if (!token) { showAuthAlert(); return; }
    setCurrentReportType(reportType);
    setIsFullAnalysisModalOpen(true);
  };

  const handleFullAnalysisClose = () => setIsFullAnalysisModalOpen(false);

  const handleFavoriteToggle = async () => {
    if (!symbol) return;
    setFavoriteLoading(true);
    try {
      if (!isOAuthUser()) { showAuthAlert(); return; }
      // 백엔드 PUT /api/v1/auth/favorite 는 토글 동작 — body { ticker } 만 전송
      const newFavoriteState = !isFavorite;
      await toggleFavorite(symbol);
      setIsFavorite(newFavoriteState);
    } catch (e) {
      console.error('Error toggling favorite:', e);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // ── data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAllData = async () => {
      if (!symbol) return;
      try {
        setLoading(true);
        setError(null);

        const runAt = await getCoinDetailRunAt(symbol, timeFrame).catch(() => null);

        const [
          summaryRes,
          technicalRes,
          communityRes,
          riskRes,
          detailTechRes,
          detailCommRes,
          detailRiskRes,
          detailSummaryRes,
        ] = await Promise.allSettled([
          getSummaryReport(symbol, timeFrame, runAt),
          getTechnicalAnalysis(symbol, timeFrame, runAt),
          getCommunityAnalysis(symbol, timeFrame, runAt),
          getRiskAnalysis(symbol, timeFrame, runAt),
          getCoinDetailTechnical(symbol, timeFrame, runAt),
          getCoinDetailCommunity(symbol, timeFrame, runAt),
          getCoinDetailRisk(symbol, timeFrame, runAt),
          getCoinDetailSummary(symbol, timeFrame, runAt),
        ]);

        if (summaryRes.status === 'fulfilled')      setSummaryData(summaryRes.value);
        if (technicalRes.status === 'fulfilled')    setTechnicalData(technicalRes.value);
        if (communityRes.status === 'fulfilled')    setCommunityData(communityRes.value);
        if (riskRes.status === 'fulfilled')         setRiskData(riskRes.value);
        if (detailTechRes.status === 'fulfilled')   setStockDetailTechnical(detailTechRes.value);
        if (detailCommRes.status === 'fulfilled')   setStockDetailCommunity(detailCommRes.value);
        if (detailRiskRes.status === 'fulfilled')   setStockDetailRisk(detailRiskRes.value);
        if (detailSummaryRes.status === 'fulfilled') setStockDetailSummary(detailSummaryRes.value);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [symbol, timeFrame]);

  // timeFrame 변경 시 URL 동기화 (?tf=)
  useEffect(() => {
    const urlTf = searchParams.get('tf');
    if (urlTf !== timeFrame) {
      const next = new URLSearchParams(searchParams);
      next.set('tf', timeFrame);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrame]);

  useEffect(() => {
    if (isOAuthUser()) checkFavoriteStatus();
  }, [symbol]);

  // ── community pagination ───────────────────────────────────────────────────
  const COMMUNITY_PER_PAGE = 3;

  // 백엔드 응답: stockDetailCommunity.communityDataDto.community24h: [{ postId, postTitle, postContent, postScore, postNumComments, postCreatedAt, postPermalink, source, subreddit, ... }]
  const getCommunityPosts = () => stockDetailCommunity?.communityDataDto?.community24h || [];

  const getCommunityPaginatedPosts = () => {
    const posts = getCommunityPosts();
    const start = (communityCurrentPage - 1) * COMMUNITY_PER_PAGE;
    return posts.slice(start, start + COMMUNITY_PER_PAGE);
  };

  const getCommunityTotalPages = () => {
    const posts = getCommunityPosts();
    return Math.ceil(posts.length / COMMUNITY_PER_PAGE);
  };

  const generatePageNumbers = (currentPage, totalPages) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (currentPage >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  // ── error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle size={40} className="text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">데이터를 불러올 수 없습니다</h2>
          <p className="text-gray-600 mb-8">잠시 후 다시 시도하거나 메인 페이지로 돌아가 주세요.</p>
          <button onClick={() => window.location.reload()} className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors">
            페이지 새로고침
          </button>
        </div>
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="detail-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-row">
            <div className="logo-container">
              <button className="back-button" onClick={handleBack}><ArrowLeft size={20} /></button>
            </div>
            <div className="header-actions">
              <div
                className="icon-button"
                role="button" tabIndex={0}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openSearchModal(e); }}
                style={{ cursor: 'pointer' }}
              >
                <Search size={16} />
              </div>
              <button type="button" className="icon-button" onClick={handleFavoriteToggle} disabled={favoriteLoading}>
                <Star
                  size={16}
                  fill={isFavorite ? "#f59e0b" : "none"}
                  className={`transition-colors ${isFavorite ? 'text-yellow-400' : ''} ${favoriteLoading ? 'opacity-50' : ''}`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="detail-content">

        {/* TradingView Chart */}
        <div className="chart-container">
          <DetailTradingView symbol={symbol} />
        </div>

        {/* ── AI 종합 분석 ─────────────────────────────────────────────────── */}
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon"><Brain size={20} /></div>
                <h2 className="text-xm font-bold text-black">AI 종합 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                인공지능이 기술 지표, 커뮤니티 심리, 리스크 데이터를 종합적으로 분석하여 투자 인사이트를 제공합니다.
              </p>
            </div>
            {loading ? (
              <div className="animate-pulse"><div className="w-27 h-27 rounded-full bg-gray-200"></div></div>
            ) : (
              <AnimatedCircularProgress
                key={`summary-${symbol}-${stockDetailSummary?.score ?? 0}`}
                score={stockDetailSummary?.score || 0}
                label={stockDetailSummary?.recommendedPosition || "분석중"}
                size={108}
                delay={300}
              />
            )}
          </div>

          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('AI 종합 분석', stockDetailSummary?.summary)} disabled={!stockDetailSummary?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{stockDetailSummary?.summary || `${symbol} AI 종합 분석을 불러오는 중입니다.`}</p>
              {stockDetailSummary?.hashtag?.length > 0 && (
                <div className="ai-hashtag-row">
                  {stockDetailSummary.hashtag.map((kw, i) => <span key={i} className="ai-hashtag">{kw}</span>)}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick('overall')}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

          <TradingJournal
            symbol={symbol}
            scoreHistory={stockDetailSummary?.scoreHistory || []}
            scoreChartData={[]}
            simulationResults={stockDetailSummary?.simulationResults || []}
            monitoringDayCount={stockDetailSummary?.monitoringDayCount || 0}
            totalProfit={stockDetailSummary?.totalProfit || 0}
          />
        </div>

        {/* ── 기술적 분석 ──────────────────────────────────────────────────── */}
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon"><TrendingUp size={20} /></div>
                <h2 className="text-xm font-bold text-black">기술적 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                주요 기술 지표를 분석하여 현재 코인 가격의 추세와 향후 방향성을 파악합니다.
              </p>
            </div>
            {loading ? (
              <div className="animate-pulse"><div className="w-27 h-27 rounded-full bg-gray-200"></div></div>
            ) : (
              <AnimatedCircularProgress
                key={`technical-${symbol}-${stockDetailTechnical?.score ?? 0}`}
                score={stockDetailTechnical?.score || 0}
                label={stockDetailTechnical?.recommendedPosition || "분석중"}
                size={108}
                delay={300}
              />
            )}
          </div>

          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('기술적 분석', stockDetailTechnical?.summary)} disabled={!stockDetailTechnical?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{stockDetailTechnical?.summary || `${symbol} 기술적 분석 결과`}</p>
              {stockDetailTechnical?.hashtag?.length > 0 && (
                <div className="ai-hashtag-row">
                  {stockDetailTechnical.hashtag.map((kw, i) => <span key={i} className="ai-hashtag">{kw}</span>)}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick('technical')}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

          {/* Technical indicators grid */}
          {loading ? (
            <div className="grid grid-cols-2 gap-2 mt-4 animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="tech-indicator-card">
                  <div className="flex items-center justify-between mb-1">
                    <div className="h-2.5 bg-gray-200 rounded w-14"></div>
                    <div className="h-4 bg-gray-200 rounded w-10"></div>
                  </div>
                  <div className="tech-gauge-bar"><div className="tech-gauge-fill bg-gray-200" style={{ width: '55%' }}></div></div>
                  <div className="h-6 bg-gray-200 rounded w-10 mt-2"></div>
                </div>
              ))}
            </div>
          ) : (
            // 백엔드 응답: technicalData.technicalIndicators는 시간순 캔들 배열.
            // 마지막 원소가 최신 캔들의 지표값. ADX는 응답에 없어 Stoch %K로 대체.
            (() => {
              const lastTI = stockDetailTechnical?.technicalData?.technicalIndicators?.slice(-1)[0] || {};
              return (
            <div className="grid grid-cols-2 gap-2 mt-4">
              {/* RSI */}
              {(() => {
                const rsi = lastTI.rsi14;
                const ob = rsi > 70, os = rsi < 30;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">RSI (14)</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '균형'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{ width: `${Math.min(Math.max(rsi || 0, 0), 100)}%`, background: color }}></div>
                      <div className="tech-gauge-marker" style={{ left: '30%' }}></div>
                      <div className="tech-gauge-marker" style={{ left: '70%' }}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{ color }}>{rsi != null ? rsi.toFixed(1) : '-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">/100</span>
                    </div>
                  </div>
                );
              })()}
              {/* Stochastic %K (ADX 대체 — 백엔드 미제공) */}
              {(() => {
                const stoch = lastTI.stochK;
                const ob = stoch > 80, os = stoch < 20;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">Stoch %K</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '중립'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{ width: `${Math.min(Math.max(stoch || 0, 0), 100)}%`, background: color }}></div>
                      <div className="tech-gauge-marker" style={{ left: '20%' }}></div>
                      <div className="tech-gauge-marker" style={{ left: '80%' }}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{ color }}>{stoch != null ? stoch.toFixed(1) : '-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">/100</span>
                    </div>
                  </div>
                );
              })()}
              {/* CCI (20) */}
              {(() => {
                const cci = lastTI.cci20;
                const ob = cci > 100, os = cci < -100;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                const pct = Math.min(Math.max(((cci || 0) + 200) / 4, 0), 100);
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">CCI (20)</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '중립'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{ width: `${pct}%`, background: color }}></div>
                      <div className="tech-gauge-marker" style={{ left: '25%' }}></div>
                      <div className="tech-gauge-marker" style={{ left: '50%' }}></div>
                      <div className="tech-gauge-marker" style={{ left: '75%' }}></div>
                    </div>
                    <span className="tech-indicator-value" style={{ color }}>{cci != null ? cci.toFixed(0) : '-'}</span>
                  </div>
                );
              })()}
              {/* Williams %R */}
              {(() => {
                const wr = lastTI.williamsR;
                const ob = wr > -20, os = wr < -80;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                const pct = Math.min(Math.max((wr || 0) + 100, 0), 100);
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">Williams %R</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '중립'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{ width: `${pct}%`, background: color }}></div>
                      <div className="tech-gauge-marker" style={{ left: '20%' }}></div>
                      <div className="tech-gauge-marker" style={{ left: '80%' }}></div>
                    </div>
                    <span className="tech-indicator-value" style={{ color }}>{wr != null ? wr.toFixed(1) : '-'}</span>
                  </div>
                );
              })()}
            </div>
              );
            })()
          )}

          {/* Support / Resistance */}
          {loading ? (
            <div className="card mt-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-3"></div>
              <div className="space-y-1">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="sr-level-row">
                    <div className="h-3 bg-gray-200 rounded w-8"></div>
                    <div className="h-3 bg-gray-200 rounded w-10"></div>
                    <div className="h-3 bg-gray-200 rounded w-16 ml-auto"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : (() => {
            // 지지/저항: technicalIndicators 배열 마지막 캔들의 pivot/r1/r2/s1/s2/close (소문자)
            const lastTI = stockDetailTechnical?.technicalData?.technicalIndicators?.slice(-1)[0] || {};
            const fmt = (v) => (v != null ? `$${Number(v).toFixed(2)}` : '-');
            return (
            <div className="card mt-4">
              <h3 className="text-sm font-bold mb-2 text-black">지지/저항선</h3>
              <div className="flex flex-col gap-0.5">
                <div className="sr-level-row sr-resistance-2">
                  <span className="sr-code text-red-500">R2</span>
                  <span className="sr-tag" style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626' }}>강한 저항</span>
                  <span className="sr-price text-red-600">{fmt(lastTI.r2)}</span>
                </div>
                <div className="sr-level-row sr-resistance-1">
                  <span className="sr-code text-orange-500">R1</span>
                  <span className="sr-tag" style={{ background: 'rgba(249,115,22,0.08)', color: '#ea580c' }}>저항</span>
                  <span className="sr-price text-orange-600">{fmt(lastTI.r1)}</span>
                </div>
                <div className="sr-level-row sr-pivot">
                  <span className="sr-code text-blue-500">PP</span>
                  <span className="sr-tag" style={{ background: 'rgba(59,130,246,0.08)', color: '#2563eb' }}>피벗</span>
                  <span className="sr-price text-blue-600">{fmt(lastTI.pivot)}</span>
                </div>
                <div className="sr-divider"></div>
                <div className="sr-level-row sr-current">
                  <span className="sr-code" style={{ color: '#7c6db5' }}>NOW</span>
                  <span className="sr-tag" style={{ background: 'rgba(184,167,232,0.15)', color: '#7c6db5' }}>현재가</span>
                  <span className="sr-price" style={{ color: '#7c6db5' }}>{fmt(lastTI.close)}</span>
                </div>
                <div className="sr-divider"></div>
                <div className="sr-level-row sr-support-1">
                  <span className="sr-code text-green-500">S1</span>
                  <span className="sr-tag" style={{ background: 'rgba(34,197,94,0.08)', color: '#16a34a' }}>지지</span>
                  <span className="sr-price text-green-600">{fmt(lastTI.s1)}</span>
                </div>
                <div className="sr-level-row sr-support-2">
                  <span className="sr-code text-green-700">S2</span>
                  <span className="sr-tag" style={{ background: 'rgba(22,163,74,0.08)', color: '#15803d' }}>강한 지지</span>
                  <span className="sr-price text-green-700">{fmt(lastTI.s2)}</span>
                </div>
              </div>
            </div>
            );
          })()}
        </div>

        {/* ── 커뮤니티 ─────────────────────────────────────────────────────── */}
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon"><MessageSquare size={16} /></div>
                <h2 className="text-xm font-bold text-black">커뮤니티</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1 leading-relaxed">
                코인의 흐름은 결국 사람들의 관심과 열정(passion)에서 시작됩니다.<br />
                개인 투자자들의 반응을 실시간으로 파악해 시장의 과열 여부를 가늠하는 지표로 활용하세요.
              </p>
            </div>
            {loading ? (
              <div className="animate-pulse"><div className="w-27 h-27 rounded-full bg-gray-200"></div></div>
            ) : (
              <AnimatedCircularProgress
                key={`community-${symbol}-${stockDetailCommunity?.score ?? 0}`}
                score={stockDetailCommunity?.score || 0}
                label={stockDetailCommunity?.recommendedPosition || "분석중"}
                size={108}
                delay={300}
              />
            )}
          </div>

          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('커뮤니티 분석', stockDetailCommunity?.summary)} disabled={!stockDetailCommunity?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{stockDetailCommunity?.summary || `${symbol} 커뮤니티 분석 결과`}</p>
              {stockDetailCommunity?.hashtag?.length > 0 && (
                <div className="ai-hashtag-row">
                  {stockDetailCommunity.hashtag.map((kw, i) => <span key={i} className="ai-hashtag">{kw}</span>)}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick('community')}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

          {/* Community posts */}
          {loading ? (
            <>
              {[0, 1].map(i => (
                <div key={i} className="card mt-4 animate-pulse">
                  <div className="flex items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                        <div className="h-3 bg-gray-200 rounded w-10 ml-auto"></div>
                      </div>
                      <div className="h-3 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded mb-1 w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : getCommunityPaginatedPosts().length > 0 ? (
            <>
              {getCommunityPaginatedPosts().map((post, index) => (
                <div key={post.postId || index} className="card mt-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="#FF4500">
                          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                        </svg>
                      </div>
                      <span className="text-xs text-gray-500 ml-2">
                        {new Date(post.postCreatedAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className={`ml-auto text-xs px-2 py-1 rounded ${
                        parseInt(post.postScore) > 1000 ? 'bg-green-100 text-green-600' :
                        parseInt(post.postScore) > 100  ? 'bg-blue-100 text-blue-600'  : 'bg-gray-100 text-gray-600'
                      }`}>
                        추천수: {post.postScore}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold mt-1 mb-1 break-words overflow-hidden">{post.postTitle}</h4>
                    {post.postContent && (
                      <p className="text-xs text-gray-700 mt-1 mb-2 break-words overflow-hidden">
                        {post.postContent.substring(0, 100)}{post.postContent.length > 100 ? '...' : ''}
                      </p>
                    )}
                    <div className="flex mt-2 justify-between items-center">
                      <div className="bg-gray-100 rounded-full px-2 py-1 text-xs flex items-center text-gray-600">
                        <MessageSquare size={12} className="mr-1" /> {post.postNumComments}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.preventDefault(); handleTranslate(`${post.postTitle}${post.postContent ? '\n\n' + post.postContent : ''}`); }}
                          className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 cursor-pointer bg-transparent border-none p-0 transition-colors"
                        >
                          <Languages size={16} />
                        </button>
                        {post.postPermalink && (
                          <button
                            onClick={(e) => { e.preventDefault(); handleExternalLink(`https://reddit.com${post.postPermalink}`); }}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center cursor-pointer bg-transparent border-none p-0 transition-colors"
                          >
                            <CircleChevronRight size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {getCommunityTotalPages() > 1 && (
                <div className="flex items-center justify-center mt-6 space-x-2">
                  <button
                    onClick={() => setCommunityCurrentPage(p => p - 1)}
                    disabled={communityCurrentPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <div className="flex space-x-1">
                    {generatePageNumbers(communityCurrentPage, getCommunityTotalPages()).map((page, i) =>
                      page === '...' ? (
                        <span key={`d${i}`} className="px-2 py-1 text-xs text-gray-400">...</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCommunityCurrentPage(page)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all ${
                            communityCurrentPage === page
                              ? 'bg-[#B8A7E8] text-white shadow-[0_2px_8px_rgba(184,167,232,0.35)]'
                              : 'text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setCommunityCurrentPage(p => p + 1)}
                    disabled={communityCurrentPage === getCommunityTotalPages()}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="card mt-4">
              <div className="text-center py-4">
                <p className="text-gray-500">커뮤니티 데이터를 불러올 수 없습니다.</p>
              </div>
            </div>
          )}
        </div>

        {/* ── 리스크 분석 ──────────────────────────────────────────────────── */}
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon"><BarChart2 size={16} /></div>
                <h2 className="text-xm font-bold text-black">리스크 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                현재 코인의 리스크를 확인하세요. 변동성, 시장 심리, 온체인 리스크를 종합적으로 평가합니다.
              </p>
            </div>
            {loading ? (
              <div className="animate-pulse"><div className="w-27 h-27 rounded-full bg-gray-200"></div></div>
            ) : (
              <AnimatedCircularProgress
                key={`risk-${symbol}-${stockDetailRisk?.score ?? 0}`}
                score={stockDetailRisk?.score || 0}
                label={stockDetailRisk?.recommendedPosition || "분석중"}
                size={108}
                delay={300}
              />
            )}
          </div>

          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('리스크 분석', stockDetailRisk?.summary)} disabled={!stockDetailRisk?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{stockDetailRisk?.summary || `${symbol} 리스크 분석 결과`}</p>
              {stockDetailRisk?.hashtag?.length > 0 && (
                <div className="ai-hashtag-row">
                  {stockDetailRisk.hashtag.map((kw, i) => <span key={i} className="ai-hashtag">{kw}</span>)}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick('risk')}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

          {/* Risk detail data: derivativesSnapshots 배열 마지막 스냅샷 = 최신 perp 지표 */}
          {!loading && stockDetailRisk && (() => {
            const lastDeriv = stockDetailRisk?.riskData?.derivativesSnapshots?.slice(-1)[0] || {};
            const lastFund = stockDetailRisk?.riskData?.fundamentalSnapshots?.slice(-1)[0] || {};

            const lsr = lastDeriv.longShortRatio;
            const fundingRate = lastDeriv.fundingRate;
            const oiUsd = lastDeriv.openInterestValue;
            const basis = lastDeriv.basis;
            const taker = lastDeriv.takerBuySellRatio;
            const markPrice = lastDeriv.markPrice;
            const change24h = lastFund.percentChange24h;

            const fmtUsdShort = (v) => {
              if (v == null) return '-';
              const n = Number(v);
              if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
              if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
              if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
              return `$${n.toFixed(2)}`;
            };
            const fmtPct = (v, d = 2) => (v == null ? '-' : `${(Number(v) * 100).toFixed(d)}%`);
            const fmtNum = (v, d = 4) => (v == null ? '-' : Number(v).toFixed(d));
            const fmtSignedPct = (v) => (v == null ? '-' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`);

            const lsrBias = lsr == null ? '-' : lsr < 0.8 ? '숏 우위' : lsr > 1.2 ? '롱 우위' : '중립';
            const lsrColor = lsr == null ? '#9ca3af' : lsr < 0.8 ? '#ef4444' : lsr > 1.2 ? '#10b981' : '#6b7280';

            const fundingBias = fundingRate == null ? '-' : fundingRate > 0 ? '롱 비용' : fundingRate < 0 ? '숏 비용' : '중립';
            const fundingColor = fundingRate == null ? '#9ca3af' : fundingRate > 0 ? '#ef4444' : fundingRate < 0 ? '#10b981' : '#6b7280';

            const basisBias = basis == null ? '-' : basis > 0 ? '콘탱고' : '백워데이션';
            const basisColor = basis == null ? '#9ca3af' : basis > 0 ? '#10b981' : '#ef4444';

            const changeColor = change24h == null ? '#9ca3af' : change24h >= 0 ? '#10b981' : '#ef4444';

            const rows = [
              { label: 'Long/Short 비율', value: fmtNum(lsr, 4), sub: lsrBias, color: lsrColor },
              { label: '펀딩비 (8h)', value: fmtPct(fundingRate, 4), sub: fundingBias, color: fundingColor },
              { label: '미결제약정(OI)', value: fmtUsdShort(oiUsd), sub: lastDeriv.exchange || '-', color: '#3b82f6' },
              { label: '베이시스', value: fmtUsdShort(basis), sub: basisBias, color: basisColor },
              { label: 'Taker B/S 비율', value: fmtNum(taker, 3), sub: taker == null ? '-' : taker > 1 ? '매수 우세' : taker < 1 ? '매도 우세' : '균형', color: taker == null ? '#9ca3af' : taker > 1 ? '#10b981' : '#ef4444' },
              { label: 'Mark Price', value: fmtUsdShort(markPrice), sub: fmtSignedPct(change24h), color: changeColor },
            ];

            return (
              <div className="card mt-4">
                <h4 className="text-sm font-semibold mb-3 text-black">리스크 세부 지표</h4>
                <div className="grid grid-cols-2 gap-3">
                  {rows.map((r) => (
                    <div key={r.label} className="flex flex-col gap-0.5">
                      <span className="text-xs text-gray-500">{r.label}</span>
                      <span className="text-sm font-semibold text-gray-800">{r.value}</span>
                      <span className="text-[10px] font-medium" style={{ color: r.color }}>{r.sub}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        <InvestmentDisclaimer />
      </div>

      <FullAnalysisModal
        isOpen={isFullAnalysisModalOpen}
        onClose={handleFullAnalysisClose}
        stockSymbol={symbol}
        reportType={currentReportType}
      />

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
        onStockClick={handleStockClick}
      />

      <ContactFooter />
      <AlertModal alert={alert} onClose={closeAlert} />
    </div>
  );
};

export default CoinDetailPage;
